'use client'

import { useEffect, useState } from 'react'
import Script from 'next/script'
import { useCachedUser } from '@/hooks/useCachedUser'

/**
 * Atrium's own UA pixel — installed on every page when in production.
 *
 * Renders nothing (no-op) when:
 *   - NEXT_PUBLIC_ATRIUM_BRAND_ID is unset (e.g. local, preview without the env)
 *   - The current visitor is an Atrium team member (@atrium.{run,bot,watch})
 *     — their browsing pollutes our own audience signal.
 *   - Auth state is still loading — we wait to know who they are before firing.
 *   - The visitor has set localStorage `__atrium_optout` (the opt-out flag also
 *     honored by a.js itself). Belt-and-suspenders: skipping the script tag
 *     here saves a network request even when a.js would self-suppress.
 *
 * Production gating happens at the call site in `app/layout.tsx` via
 * NODE_ENV === 'production', mirroring the existing GTM injection.
 */
export function AtriumPixel() {
  const brandId = process.env.NEXT_PUBLIC_ATRIUM_BRAND_ID
  const { user, isLoading } = useCachedUser()
  const [isOptedOut, setIsOptedOut] = useState(false)

  useEffect(() => {
    try { setIsOptedOut(window.localStorage.getItem('__atrium_optout') === '1') } catch { /* ignore */ }
  }, [])

  if (!brandId) return null
  if (isLoading) return null
  if (isOptedOut) return null

  const email = user?.email ?? ''
  const isAtriumTeam =
    email.endsWith('@atrium.run') ||
    email.endsWith('@atrium.bot') ||
    email.endsWith('@atrium.watch')
  if (isAtriumTeam) return null

  return (
    <Script
      src="https://t.atrium.run/a.js"
      data-brand={brandId}
      strategy="afterInteractive"
    />
  )
}
